import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static values = {
    defaultSteps: Array,
    pipelineSteps: Array,
  };

  static targets = [
    "available",
    "selected",
    "inputs",
    "custom",
    "iconMenu",
    "iconPreview",
    "iconInput",
    "customName",
    "customCategory",
  ];

  // CONNECT
  connect() {
    this.initiDefaultSteps();
  }

  // HANDLES
  select(element, isLocked = false) {
    const el = element.currentTarget || element;
    const id = el.dataset.stepId;

    // se já está selecionado → remove
    if (this.selectedIds.includes(id)) {
      this.unselectDefault(el, id);
      return;
    }

    this.selectedIds.push(id);

    el.remove();
    this.selectedTarget.appendChild(el);

    el.classList.remove("tag-light");
    el.classList.add("tag-selected");
    if (isLocked) {
      el.classList.add("locked");
    }

    el.setAttribute("draggable", "true");
    el.dataset.stepPosition = this.selectedTarget.children.length + 1;
    el.dataset.action = `dragstart->pipeline-steps#dragStart dragover->pipeline-steps#dragOver drop->pipeline-steps#drop ${
      isLocked ? "" : "click->pipeline-steps#select"
    }`;

    this.addHidden(id);
    this.updatePositions();
  }

  unselectDefault(el, id) {
    // array
    this.selectedIds = this.selectedIds.filter((v) => v !== id);

    // hidden input
    this.inputsTarget
      .querySelectorAll(`input[value="${id}"]`)
      .forEach((i) => i.remove());

    // volta pra available
    el.remove();
    this.availableTarget.insertBefore(
      el,
      this.availableTarget.lastElementChild
    );

    el.classList.remove("tag-selected");
    el.classList.add("tag-light");

    // continua clicável
    el.dataset.action = "click->pipeline-steps#select";

    this.updatePositions();
  }

  addHidden(id) {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = "ats_pipeline[step_ids][]";
    input.value = id;

    this.inputsTarget.appendChild(input);
  }

  showCustom() {
    this.customTarget.classList.remove("d-none");
  }

  hideCustom() {
    this.customTarget.classList.add("d-none");
  }

  addCustom(event) {
    event.preventDefault();

    const name = this.customNameTarget.value.trim();
    if (!name) return;

    const icon = this.iconInputTarget.value || "fa-search";
    const category = this.customCategoryTarget.value;
    const position = this.selectedTarget.children.length + 1;
    const uid = crypto.randomUUID();
    const badge = document.createElement("span");

    badge.addEventListener("click", () => this.removeCustom(uid, badge));
    badge.className = "badge tag-selected d-flex align-items-center gap-1";
    badge.innerHTML = `<i class="fa-solid ${icon} me-1"></i>${name}`;

    badge.dataset.customUid = uid;
    badge.dataset.stepPosition = position;
    badge.dataset.name = name;
    badge.dataset.icon = icon;
    badge.dataset.category = category;

    badge.setAttribute("draggable", "true");
    badge.dataset.action =
      "dragstart->pipeline-steps#dragStart dragover->pipeline-steps#dragOver drop->pipeline-steps#drop";

    this.selectedTarget.appendChild(badge);
    this.inputsTarget.insertAdjacentHTML(
      "beforeend",
      `
      <input type="hidden" data-custom-uid="${uid}" name="ats_pipeline[custom_steps][][name]" value="${name}">
      <input type="hidden" data-custom-uid="${uid}" name="ats_pipeline[custom_steps][][icon]" value="${icon}">
      <input type="hidden" data-custom-uid="${uid}" name="ats_pipeline[custom_steps][][category]" value="${category}">
      <input type="hidden" data-custom-uid="${uid}" name="ats_pipeline[custom_steps][][position]" value="${position}">
    `
    );

    this.customNameTarget.value = "";
    this.iconInputTarget.value = "";
    this.iconPreviewTarget.className = "fa-solid fa-search fs-4";
    this.customTarget.classList.add("d-none");

    this.updatePositions();
  }

  removeCustom(uid, badge) {
    badge.remove();

    this.inputsTarget
      .querySelectorAll(`[data-custom-uid="${uid}"]`)
      .forEach((i) => i.remove());

    this.updatePositions();
  }

  toggleIcons(event) {
    event.preventDefault();
    this.iconMenuTarget.classList.toggle("show");
  }

  pickIcon(event) {
    event.preventDefault();

    const icon = event.currentTarget.dataset.icon;
    this.iconPreviewTarget.className = `fa-solid ${icon} fs-4`;
    this.iconInputTarget.value = icon;
    this.iconMenuTarget.classList.remove("show");
  }

  dragStart(event) {
    this.draggedElement = event.currentTarget;
    event.dataTransfer.effectAllowed = "move";
  }

  dragOver(event) {
    event.preventDefault();
    event.dataTransfer.dropEffect = "move";
  }

  drop(event) {
    event.preventDefault();

    const target = event.currentTarget;
    if (!this.draggedElement || target === this.draggedElement) return;

    const oldPositions = this.getPositions();

    const rect = target.getBoundingClientRect();
    const isAfter = event.clientY > rect.top + rect.height / 2;

    isAfter
      ? target.after(this.draggedElement)
      : target.before(this.draggedElement);

    this.animateFlip(oldPositions);
    this.updatePositions();
  }

  // HELPERS
  initiDefaultSteps() {
    this.selectedIds = [];
    const mandatoryStepKinds = [
      "profile_registration",
      "application",
      "rejected",
      "hired",
    ];

    if (this.pipelineStepsValue?.length) {
      this.pipelineStepsValue.forEach((step) => {
        const isLocked = mandatoryStepKinds.includes(step.kind);
        this.select(
          this.availableTarget.querySelector(`[data-step-id="${step.id}"]`),
          isLocked
        );
      });
    } else {
      this.defaultStepsValue.forEach((step) => {
        if (mandatoryStepKinds.includes(step.kind)) {
          const isLocked = true;
          this.select(
            this.availableTarget.querySelector(`[data-step-id="${step.id}"]`),
            isLocked
          );
        }
      });
    }
  }

  updatePositions() {
    this.inputsTarget.innerHTML = "";

    Array.from(this.selectedTarget.children).forEach((el, index) => {
      const position = index + 1;
      el.dataset.stepPosition = position;

      // DEFAULT STEP
      if (el.dataset.stepId) {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = "ats_pipeline[step_ids][]";
        input.value = el.dataset.stepId;
        this.inputsTarget.appendChild(input);
      }

      // CUSTOM STEP
      if (el.dataset.customUid) {
        const uid = el.dataset.customUid;

        this.inputsTarget.insertAdjacentHTML(
          "beforeend",
          `
					<input type="hidden" data-custom-uid="${uid}" name="ats_pipeline[custom_steps][][name]" value="${el.dataset.name}">
					<input type="hidden" data-custom-uid="${uid}" name="ats_pipeline[custom_steps][][icon]" value="${el.dataset.icon}">
					<input type="hidden" data-custom-uid="${uid}" name="ats_pipeline[custom_steps][][category]" value="${el.dataset.category}">
					<input type="hidden" data-custom-uid="${uid}" name="ats_pipeline[custom_steps][][position]" value="${position}">
					`
        );
      }
    });
  }

  getPositions() {
    const positions = new Map();

    Array.from(this.selectedTarget.children).forEach((el) => {
      positions.set(el, el.getBoundingClientRect());
    });

    return positions;
  }

  animateFlip(oldPositions) {
    Array.from(this.selectedTarget.children).forEach((el) => {
      const oldPos = oldPositions.get(el);
      if (!oldPos) return;

      const newPos = el.getBoundingClientRect();

      const dx = oldPos.left - newPos.left;
      const dy = oldPos.top - newPos.top;

      if (!dx && !dy) return;

      // INVERT
      el.style.transform = `translate(${dx}px, ${dy}px)`;
      el.style.transition = "none";

      // FORÇA REFLOW
      el.offsetHeight;

      // PLAY
      requestAnimationFrame(() => {
        el.style.transition = "transform 200ms ease";
        el.style.transform = "translate(0, 0)";
      });
    });
  }
}
